import React, { useReducer, useCallback, useEffect } from 'react';
import { useWizardService, getWizardData, WIZARD_EVENTS } from '@bingads-webui-fluent-component/wizard';
import { useInstrumentLogger } from '@bingads-webui-campaign-react/linkedin-instrumentation';

import { useErrorDialog, useErrorMessageBar } from './hooks';
import { AuthenticationStage, CompanyPageSelectionStage, RequestAccessStage, PermissionCheckStageV2, MemberTokenRefreshStage } from './stages';
import { getInitialState, companyPageLinkingReducer } from './reducer';
import { getCurrentLinkingStage } from './utils';
import { LINKING_STAGES } from './constants';

export const CompanyPageLinkingStep = () => {
  const logger = useInstrumentLogger({
    scenarioName: 'CompanyPageLinkingStep',
    activityName: 'Logger',
  });
  const { state: wizardState, send } = useWizardService();
  const {
    permissionStatus, connectedCompanyPage, isMemberTokenActive, isReAuthOnly,
  } = getWizardData(wizardState);
  const initialState = getInitialState(permissionStatus, connectedCompanyPage, isMemberTokenActive, isReAuthOnly);
  const [state, dispatch] = useReducer(companyPageLinkingReducer, initialState);

  const {
    isAuthenticating,
    isSavingCompanyPage,
    isLoadingPermissionStatus,
    isResendingPermissionRequest,
    isRefreshingToken,
    needRefreshMemberToken,
  } = state;
  // Condition for trigger processing blocker
  const isProcessing = isAuthenticating || isLoadingPermissionStatus || isResendingPermissionRequest || isRefreshingToken || (needRefreshMemberToken && isMemberTokenActive);

  const currentLinkingStage = getCurrentLinkingStage(state);
  const errorDialog = useErrorDialog({ state, dispatch });
  const serverError = useErrorMessageBar({ state });

  const onCancel = useCallback(() => {
    logger.trace('Exit wizard from company page linking step');
    send(WIZARD_EVENTS.EXIT);
  }, [logger, send]);

  const onContinue = useCallback(() => {
    logger.trace('Finish company page linking step');
    send(WIZARD_EVENTS.NEXT);
    send(WIZARD_EVENTS.UPDATE_FlOW_FACTORS, {
      flowFactors: { isCompanyPageReady: true },
    });
  }, [logger, send]);

  useEffect(() => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isProcessing, isSavingCompanyPage });
  }, [isProcessing, isSavingCompanyPage, send]);

  return (
    <div className="company-page-linking-step">
      {currentLinkingStage === LINKING_STAGES.MEMBER_TOKEN_REFRESH &&
        <MemberTokenRefreshStage
          state={state}
          dispatch={dispatch}
          onCancel={onCancel}
          onContinue={onContinue}
          serverError={serverError}
          logger={logger}
        />
      }
      {currentLinkingStage === LINKING_STAGES.LINKEDIN_AUTHENTICATION &&
        <AuthenticationStage
          state={state}
          dispatch={dispatch}
          onCancel={onCancel}
          serverError={serverError}
          logger={logger}
        />
      }
      {currentLinkingStage === LINKING_STAGES.COMPANY_PAGE_SELECTION &&
        <CompanyPageSelectionStage
          state={state}
          dispatch={dispatch}
          onCancel={onCancel}
          onContinue={onContinue}
          serverError={serverError}
          logger={logger}
        />
      }
      {currentLinkingStage === LINKING_STAGES.REQUEST_ACCESS &&
        <RequestAccessStage
          state={state}
          dispatch={dispatch}
          onContinue={onContinue}
          serverError={serverError}
          logger={logger}
        />
      }
      {currentLinkingStage === LINKING_STAGES.PERMISSION_CHECK &&
        <PermissionCheckStageV2
          state={state}
          dispatch={dispatch}
          onContinue={onContinue}
          serverError={serverError}
          logger={logger}
        />
      }
      {errorDialog}
    </div>
  );
};
